/**
 * The legacy app's bottom bar icon set (ISSUE-962). The web app ships no
 * icon library, so these are inline SVG, the same technique
 * `Composer.tsx` already uses for its action buttons. Every icon renders
 * hidden by default; the phone-width rule in `apps/app/src/index.css`
 * is what shows it. ISSUE-965 adds the person, gear and clipboard check
 * icons for the pool screen's tabs, drawn the same way; those take their
 * visibility from the caller, since the shared `Tabs` shows them at phone
 * width only.
 */
import type { ReactNode } from "react";

import type { typeWebSectionKey } from "./sections";

const ICON_SVG_PROPS = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "1.75",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
  focusable: "false",
} as const;

export const ScoreboardIcon = ({
  className,
}: {
  className: string;
}): ReactNode => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    data-section-icon="scores"
    {...ICON_SVG_PROPS}
  >
    <rect x="3" y="6" width="18" height="14" rx="2" />
    <path d="M12 6v14M8 3.5V6M16 3.5V6" />
    <path d="M6.5 10.5 8 9v8" />
    <rect x="14.75" y="9" width="3.5" height="8" rx="1.75" />
  </svg>
);

export const TrophyIcon = ({ className }: { className: string }): ReactNode => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    data-section-icon="pools"
    {...ICON_SVG_PROPS}
  >
    <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
    <path d="M7 5.5H4.5V7a3 3 0 0 0 3 3M17 5.5h2.5V7a3 3 0 0 1-3 3" />
    <path d="M12 14v6M8 20h8" />
  </svg>
);

export const PersonPlusIcon = ({
  className,
}: {
  className: string;
}): ReactNode => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    data-section-icon="invites"
    {...ICON_SVG_PROPS}
  >
    <circle cx="10" cy="8" r="4" />
    <path d="M3 20a7 7 0 0 1 14 0" />
    <path d="M19 8v6M16 11h6" />
  </svg>
);

export const ChatDotsIcon = ({
  className,
}: {
  className: string;
}): ReactNode => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    data-section-icon="messages"
    {...ICON_SVG_PROPS}
  >
    <path d="M12 3a9 9 0 0 0-7.8 13.5L3 21l4.6-1.2A9 9 0 1 0 12 3Z" />
    <circle cx="8" cy="12" r="1.25" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.25" fill="currentColor" stroke="none" />
    <circle cx="16" cy="12" r="1.25" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * The phone bar's Help item, which opens the support chat. A question mark in
 * a circle, drawn on the same 24px grid and stroke as the four section icons.
 */
export const HelpCircleIcon = ({
  className,
}: {
  className: string;
}): ReactNode => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    data-icon="help"
    {...ICON_SVG_PROPS}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.27-1 .87-1 1.53v.67" />
    <circle cx="12" cy="17" r="1.25" fill="currentColor" stroke="none" />
  </svg>
);

export const PersonIcon = ({ className }: { className: string }): ReactNode => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    data-icon="person"
    {...ICON_SVG_PROPS}
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20a8 8 0 0 1 16 0" />
  </svg>
);

export const GearIcon = ({ className }: { className: string }): ReactNode => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    data-icon="gear"
    {...ICON_SVG_PROPS}
  >
    <circle cx="12" cy="12" r="6.5" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.28 5.28l2.12 2.12M16.6 16.6l2.12 2.12M5.28 18.72l2.12-2.12M16.6 7.4l2.12-2.12" />
  </svg>
);

export const ClipboardCheckIcon = ({
  className,
}: {
  className: string;
}): ReactNode => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    data-icon="clipboard-check"
    {...ICON_SVG_PROPS}
  >
    <rect x="5" y="4" width="14" height="17" rx="2" />
    <rect x="9" y="2.5" width="6" height="3" rx="1" />
    <path d="m9 13 2 2 4-4" />
  </svg>
);

export const SectionIcon = ({
  sectionKey,
}: {
  sectionKey: typeWebSectionKey;
}): ReactNode => {
  const className = "hidden size-icon-lg shrink-0";
  switch (sectionKey) {
    case "scores":
      return <ScoreboardIcon className={className} />;
    case "pools":
      return <TrophyIcon className={className} />;
    case "invites":
      return <PersonPlusIcon className={className} />;
    case "messages":
      return <ChatDotsIcon className={className} />;
  }
};
